// types.
import { LeadDetail } from '@domains/lead-detail/interfaces/interfaces';
import { GeoreferencingOverrides } from '../interfaces/georeferencing.types';
import { buildChangeCoordinatesPayload } from './buildChangeCoordinatesPayload';

export interface LoadingHandlers {
  setIsLoading: (loading: boolean) => void;
  refetchLeadDetail: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export interface GeoreferencingCallbacks {
  handleGeoreferencingSuccess: (
    setIsLoading: (loading: boolean) => void,
    refetchLeadDetail: () => void,
    onSuccess?: () => void
  ) => Promise<void>;
  handleGeoreferencingError: (
    setIsLoading: (loading: boolean) => void,
    errorMessage: string,
    onError?: (error: string) => void
  ) => void;
}

export interface LeadData {
  leadDetail: LeadDetail | undefined;
  coordinates: google.maps.LatLngLiteral | null;
  email: string;
  country: string | undefined;
  overrides?: GeoreferencingOverrides;
}

export interface ProcessWebSocketMessageParams {
  parsedBody: any;
  selectedBusinessLine: string | null;
  loadingHandlers: LoadingHandlers;
  callbacks: GeoreferencingCallbacks;
  leadData: LeadData;
  saveHermes: any;
}

const handleInmoFlow = async (
  loadingHandlers: LoadingHandlers,
  callbacks: GeoreferencingCallbacks
) => {
  await callbacks.handleGeoreferencingSuccess(
    loadingHandlers.setIsLoading,
    loadingHandlers.refetchLeadDetail,
    loadingHandlers.onSuccess
  );
};

const handleMarketMakerFlow = async (
  leadData: LeadData,
  saveHermes: any,
  loadingHandlers: LoadingHandlers,
  callbacks: GeoreferencingCallbacks
) => {
  if (!leadData.coordinates) {
    throw new Error('No hay coordenadas seleccionadas');
  }
  if (!leadData.email) {
    throw new Error('No se ha obtenido el email del agente');
  }

  // Refetch para obtener datos frescos antes de construir el payload de change_coordinates.
  // Evita que el cache stale de leadDetail sobreescriba campos actualizados
  // por Document Review (area, admin_price, years_old, bath, etc.).
  let currentLeadDetail = leadData.leadDetail;
  try {
    const refetchResult = await (loadingHandlers.refetchLeadDetail as () => Promise<{ data: typeof leadData.leadDetail }>)();
    if (refetchResult?.data) {
      currentLeadDetail = refetchResult.data;
      console.log('[processWebSocketMessage] 🔄 leadDetail refrescado para MM:', currentLeadDetail?.id);
    }
  } catch {
    console.warn('[processWebSocketMessage] No se pudo refetchear leadDetail, usando cache existente');
  }

  if (!currentLeadDetail) {
    throw new Error('No hay datos del lead disponibles');
  }

  const changeCoordPayload = buildChangeCoordinatesPayload(
    currentLeadDetail,
    leadData.coordinates,
    leadData.email,
    leadData.country,
    leadData.overrides
  );

  console.log('[processWebSocketMessage] 📡 Llamando change_coordinates...', changeCoordPayload);
  await saveHermes(changeCoordPayload).unwrap();
  console.log('[processWebSocketMessage] ✅ change_coordinates completado');
  await callbacks.handleGeoreferencingSuccess(
    loadingHandlers.setIsLoading,
    loadingHandlers.refetchLeadDetail,
    loadingHandlers.onSuccess
  );
};

export const processWebSocketMessage = async ({
  parsedBody,
  selectedBusinessLine,
  loadingHandlers,
  callbacks,
  leadData,
  saveHermes,
}: ProcessWebSocketMessageParams): Promise<void> => {
  // El backbone responde con 'georeferencing' para flujos de revisión de docs,
  // y con 'put_lead' para el flujo directo del módulo de Georeferenciación.
  const isGeoreferencingStep =
    parsedBody?.step === 'georeferencing' || parsedBody?.step === 'put_lead';

  if (!isGeoreferencingStep) {
    console.warn(`[processWebSocketMessage] Step no manejado: "${parsedBody?.step}". Se omite.`);
    return;
  }

  console.log(`[processWebSocketMessage] Procesando step: "${parsedBody?.step}", success: ${parsedBody?.success}, businessLine: ${selectedBusinessLine}`);

  if (parsedBody?.success === false) {
    callbacks.handleGeoreferencingError(
      loadingHandlers.setIsLoading,
      parsedBody?.error || 'Error en georreferenciación',
      loadingHandlers.onError
    );
    return;
  }

  if (parsedBody?.success !== true) {
    return;
  }

  // Success case - diferenciado por business line
  if (selectedBusinessLine === 'Inmo') {
    handleInmoFlow(loadingHandlers, callbacks);
  } else if (selectedBusinessLine === 'Market-Maker') {
    try {
      await handleMarketMakerFlow(leadData, saveHermes, loadingHandlers, callbacks);
    } catch (error: any) {
      console.error('Error en /change_coordinates:', error);
      callbacks.handleGeoreferencingError(
        loadingHandlers.setIsLoading,
        error?.message || 'Error al guardar en change_coordinates',
        loadingHandlers.onError
      );
    }
  }
};

